<template lang="pug">
    div(
        class="_plin",
        :class="{ ['_p'+position]: position, ['_a'+align]: align != 'start', _busy: busy, _slow: duration >= 1000 }",
        :style="{ width, height }",
        :inert="blocking && busy")
        transition(name="_plint", :duration="1000")
            div(
                v-show="show && ready",
                class="_plinbar",
                role="progressbar",
                :aria-valuenow="value",
                :aria-valuemin="value !== undefined ? min : undefined",
                :aria-valuemax="value !== undefined ? max : undefined")
                div(class="_plinbg")
                div(
                    class="_plinfg",
                    :class="{ _inf: value === undefined }",
                    :style="{ width: percent, 'transition-duration': duration + 'ms' }",
                    @transitionend="_done")
        slot
</template>

<script>
    /**
     * Progress indicator with linear (bar) style.
     */
    export default {
        name: 'ib-progress-linear',

        props: {
            /**
             * If `true`, block interaction with slot content unless not visible or `value == max`.
             */
            blocking: Boolean,
            /**
             * Position relative to content to display progress indicator. Valid positions are
             * `fixed`, `top`, `bottom`, and `background`.
             */
            position: {
                type: String,
                default: 'top'
            },
            /**
             * Alignment of progress indicator. Valid alignments are `start`, `center`, and `end`.
             */
            align: {
                type: String,
                default: 'start'
            },
            /**
             * If `false`, hide the progress bar, but display slot content.
             */
            show: {
                type: Boolean,
                default: true
            },
            /**
             * Current value to display; clamped to [min,max] inclusive. If not specified, the
             * progress indicator will loop continually.
             */
            value: {
                type: Number,
                default: undefined
            },
            /**
             * Minimum value that can be displayed.
             */
            min: {
                type: Number,
                default: 0
            },
            /**
             * Maximum value that can be displayed.
             */
            max: {
                type: Number,
                default: 1
            },
            /**
             * Length of time to animate value change, in millseconds.
             */
            duration: {
                type: Number,
                default: 500
            },
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        data() {
            return {
                ready: false,
                transition: false
            };
        },

        computed: {
            busy() {
                return this.show && (this.transition || this.value === undefined);
            },

            clamped() {
                return this.value === undefined ? undefined : Math.max(this.min, Math.min(this.max, this.value));
            },

            percent() {
                if (this.clamped !== undefined) {
                    return this.clamped / (this.max - this.min) * 100 + '%';
                }
            }
        },

        watch: {
            clamped() {
                if (!this.duration) {
                    this._done();
                } else {
                    this.transition = true;
                }
            }
        },

        mounted() {
            // Ensure visibility transition is displayed on mount
            setTimeout(() => {
                this.ready = true;
            }, 96);
        },

        methods: {
            /** @private */
            _done() {
                this.transition = false;
                this.$emit('change', this.clamped);
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    @keyframes _plins {
        from {
            opacity: 0;
        } to {
            opacity: 1;
        }
    }
    @keyframes _plinf {
        0% {
            left: 0;
            right: 100%;
        } 50% {
            left: 25%;
            right: 0;
        } 100% {
            left: 100%;
            right: 0;
        }
    }

    .progress-linear-color(@color) {
        ._plinfg::after {
            background-image: -webkit-linear-gradient(to right, lighten(@color, 10%), lighten(@color, 10%) calc(~'100% - 240px'), darken(@color, 10%));
            background-image:         linear-gradient(to right, lighten(@color, 10%), lighten(@color, 10%) calc(~'100% - 240px'), darken(@color, 10%));
        }
    }

    ._plin {
        position: relative;
        min-height: 3px;

        &._slow ._plinfg {
            transition-timing-function: linear;
        }
        &[inert] {
            &::before {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: fade(@bg, 60%);
                pointer-events: all;
                z-index: @z-progress;
                cursor: wait;
            }
        }

        /** position */
        &._pfixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: @z-progress-fixed;
        }
        &._ptop ._plinbar {
            height: 3px;
        }
        &._pbottom ._plinbar {
            top: auto;
            bottom: 0;
            height: 3px;
        }
        &._pbackground ._plinbar {
            z-index: -1;
            box-shadow: inset 0 4px 12px rgba(0,0,0,.02);
            ._plinbg {
                opacity: .25;
            }
            ._plinfg {
                opacity: .1;
                &::before {
                    box-shadow: 0 0 22px rgba(0,0,0,.8);
                }
            }
        }

        /** align */
        & ._plinbar {
            justify-content: flex-start;
        }
        &._acenter ._plinbar {
            justify-content: center;
        }
        &._aend ._plinbar {
            justify-content: flex-end;
        }

        /* Color classes */
        &           { .progress-linear-color(@fg-accent); }
        &.positive  { .progress-linear-color(@fg-positive); }
        &.negative  { .progress-linear-color(@fg-negative); }
        &.buy       { .progress-linear-color(@fg-buy); }
        &.hold      { .progress-linear-color(@fg-hold); }
        &.sell      { .progress-linear-color(@fg-sell); }
        &.ok        { .progress-linear-color(@fg-ok); }
        &.warning   { .progress-linear-color(@fg-warning); }
        &.error     { .progress-linear-color(@fg-error); }
    }

    ._plinbar {
        display: flex;
        position: absolute;
        pointer-events: none;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: @z-progress;
    }
    ._plinbg,
    ._plinfg {
        position: absolute;
        top: 0;
        bottom: 0;
    }
    ._plinbg {
        left: 0;
        right: 0;
        background-color: fade(mix(@fg-max, @bg, 8%), 80%);
        border-radius: 2px;
    }
    ._plinfg {
        transition: width;
        clip: rect(0, 100vw, 100vh, 0);
        &._inf {
            animation: _plinf 1.4s infinite;
        }
        &::before {
            content: '';
            position: absolute;
            bottom: 6px;
            right: 0;
            width: 116px;
            max-width: 50%;
            height: 8px;
            box-shadow: 0 0 22px rgba(0,0,0,.55);
            transform: rotate(3.5deg);
        }
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            border-radius: 2px;
        }
    }

    ._plint {
        &-enter-active {
            animation: _plins 1s;
        }
        &-leave-active {
            animation: _plins .25s reverse;
        }
    }
</style>

<docs>
    Basic Example:
    ```jsx
    <ib-context>
        <ib-progress-linear :min="0" :max="100" position="top" value="50" class="inset-8 bg10-buy">
            <p>50% Done</p>
        </ib-progress-linear>
        <ib-progress-linear :min="0" :max="100" position="top" value="25" class="inset-8 bg10-buy" position="background">
            <p>Prices as of 13:45:37 EDT</p>
        </ib-progress-linear>
    </ib-context>
    ```
</docs>
